import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Riders", to: "/riders" },
  { label: "Contact", to: "/contact" },
  { label: "Privacy", to: "/privacy-policy" },
  { label: "Terms", to: "/terms" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onLanding = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const solid = scrolled || !onLanding || open;

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid ? "border-b border-slate-900/10 bg-white/90 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center" aria-label="Fetcher home">
          <img src="/logo-orange.png" alt="Fetcher" className={`h-6 w-auto sm:h-7 ${solid ? "block" : "hidden"}`} />
          <img src="/logo-white.webp" alt="Fetcher" className={`h-6 w-auto sm:h-7 ${solid ? "hidden" : "block"}`} />
        </Link>

        {/* Desktop links */}
        <div className="hidden items-center gap-2 md:flex">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`rounded-full px-4 py-2 text-sm font-bold transition-all duration-200 hover:bg-slate-900/5 ${
                solid ? "text-slate-700 hover:text-[#D95A17]" : "text-white/90 hover:bg-white/10 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className={`grid h-10 w-10 place-items-center rounded-xl transition-colors md:hidden ${
            solid ? "text-slate-800 hover:bg-slate-900/5" : "text-white hover:bg-white/10"
          }`}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile menu panel */}
      <div
        data-testid="nav-mobile-menu"
        className={`overflow-hidden border-t border-slate-900/10 bg-white/95 backdrop-blur-md transition-[max-height,opacity] duration-300 md:hidden ${
          open ? "max-h-96 opacity-100" : "pointer-events-none max-h-0 opacity-0"
        }`}
      >
        <div className="flex flex-col px-5 py-2">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              onClick={() => setOpen(false)}
              className="rounded-xl px-3 py-3 text-base font-bold text-slate-800 transition-colors hover:bg-[#FFEDD5] hover:text-[#D95A17]"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
};
